import React from 'react';
import debounce from 'lodash/debounce';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import ReactTooltip from 'react-tooltip';

import './Tooltip.scss';

// only for single-line tooltips - tip is in the corner
// eslint-disable-next-line no-underscore-dangle
function _overridePosition(position, event, target, tooltip, place) {
  const clientRect = tooltip.getBoundingClientRect ? tooltip.getBoundingClientRect() : null;
  if (clientRect) {
    switch (place) {
      case 'left':
      case 'right':
        return {
          left: position.left,
          top: position.top - clientRect.height,
        };
      default:
        // top and bottom
        return {
          left: position.left + parseInt(clientRect.width / 2, 10),
          top: position.top,
        };
    }
  } else {
    return position;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const overridePositionTopLeft = (position, event, target, tooltip, place) => {
  if (place !== 'top') {
    return _overridePosition(position, event, target, tooltip, place);
  }
  if (!isFunction(tooltip.getBoundingClientRect) || !isFunction(target.getBoundingClientRect)) {
    return position;
  }
  const tooltipBounds = tooltip.getBoundingClientRect();
  const targetBounds = target.getBoundingClientRect();
  if (!tooltipBounds || !targetBounds) {
    return position;
  }
  return {
    left: targetBounds.left,
    top: position.top,
  };
};

export default function Tooltip(props) {
  // eslint-disable-next-line react/prop-types
  const { elsTooltip, className, effect, multiline, id, overridePosition, ...otherProps } = props;

  return (
    <ReactTooltip
      className={classnames({ 'els-tooltip': elsTooltip }, multiline ? 'els-tooltip-multiline' : '', className)}
      type="custom"
      multiline={multiline || false}
      effect={effect || 'solid'}
      insecure={false}
      id={id}
      overridePosition={overridePosition || (multiline ? null : _overridePosition)}
      disableInternalStyle
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...otherProps}
    />
  );
}

// Workaround to hide clickable tooltip while mouse still on it
export const removeClickable = (id) => {
  if (id) {
    // eslint-disable-next-line no-undef
    const el = document.querySelector(`#${id}`);
    // eslint-disable-next-line custom/no-unsafe-deep-access
    if (el) el?.classList?.remove(Tooltip.TooltipConstants.clickableClassName);
  } else {
    // eslint-disable-next-line no-undef
    const els = document.querySelectorAll('.__react_component_tooltip.show');
    const elsArr = [...els];
    elsArr.forEach((el) => {
      // eslint-disable-next-line custom/no-unsafe-deep-access
      el?.classList?.remove(Tooltip.TooltipConstants.clickableClassName);
    });
  }
};

Tooltip.TooltipConstants = {
  ids: {
    ResultListComponent: 'tooltip-result-list-component',
    ResultStructureComponent: 'tooltip-result-structure-component',
    ResultFilterComponent: 'tooltip-result-filter-component',
    AlertCreateDialog: 'tooltip-alert-create-dialog',
    Retrosynthesis: 'tooltip-for-retrosynthesis',
    RetrosynthesisParams: 'tooltip-for-retrosynthesis-params',
    RetrosynthesisParamsDialog: 'tooltip-for-retrosynthesis-params-dialog',
    RetrosynthesisEditor: 'tooltip-for-retrosynthesis-editor',
    RetrosynthesisStructureIcon: 'tooltip-for-retrosynthesis-structure-icon',
    TreeViewToolbar: 'tooltip-for-retrosynthesis-tree-icons',
    GlobalHover: 'tooltip-global-hover',
    HeatmapTooltipHeadersRows: 'heatmap-tooltip-headers-rows',
    HeatmapTooltipHeadersCols: 'heatmap-tooltip-headers-cols',
    HeatmapTooltipHeadersOptionsRows: 'heatmap-tooltip-headers-options-rows',
    HeatmapTooltipHeadersOptionsCols: 'heatmap-tooltip-headers-options-cols',
  },
  hoverableClassName: 'allow_hover',
  clickableClassName: 'allow_click',
  placeClassNames: {
    top: 'place-top',
    right: 'place-right',
    bottom: 'place-bottom',
    left: 'place-left',
  },
};
Tooltip.rebuild = debounce(ReactTooltip.rebuild);
Tooltip.hide = (...args) => {
  removeClickable();
  ReactTooltip.hide(...args);
};
Tooltip.show = ReactTooltip.show;

Tooltip.defaultProps = {
  elsTooltip: true,
};

Tooltip.propTypes = {};
